import classNames from 'classnames';
import { LIMIT_OPTIONS } from 'hooks/useAlarms';
import { Button, Container, Form } from 'react-bootstrap';
import { BsCheck } from 'react-icons/bs';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import 'styles/global.css';
import styles from './AlarmContentWrapper.module.css';

/**
 * A wrapper for alarms page content. Meant for Pending Alarms and Alarm History.
 * @param {{children: *, title: string, pageIndex: number, maxPageIndex: number, headerFields: string[], onPageChanged: (page: number), onLimitChanged: (limit: number), onSearchChanged: (search: string), silenceControlsEnabled?: boolean, allSelected?: boolean, onSelectAll?: (), onAcknowledge?: (), onSilence?: ()}}
 * @returns React Component
 */
export default function AlarmsContentWrapper({
  children,
  title,
  pageIndex,
  maxPageIndex,
  headerFields,
  onPageChanged,
  onLimitChanged,
  onSearchChanged,
  silenceControlsEnabled = false,
  allSelected = false,
  onSelectAll = () => {},
  onAcknowledge = () => {},
  onSilence = () => {},
}) {
  const setPageIndex = (newPageIndex) => {
    const boundedIndex = Math.max(0, Math.min(maxPageIndex, newPageIndex)); // Ensure the new index is within valid bounds
    onPageChanged(boundedIndex);
  };

  const setLimit = (newLimitOption) => {
    onLimitChanged(LIMIT_OPTIONS[newLimitOption]);
  };

  return (
    <Container fluid className="d-flex flex-column py-3 h-100 user-select-none text-white">
      <div className="flex-shrink-0">
        <div className="w-100 g-3 align-items-start row">
          <div className="d-flex flex-column align-items-center align-items-lg-start gap-2 col-12 col-lg-8">
            <div className="fw-bold fs-4 text-center text-lg-start w-100">{title}</div>
            <div className="d-flex flex-column gap-3 justify-content-center justify-content-lg-start">
              {silenceControlsEnabled && (
                <div className="d-flex justify-content-center justify-content-lg-start gap-3">
                  <div className="d-flex flex-column flex-column-reverse flex-md-row align-items-center gap-2">
                    <Button
                      variant={allSelected ? 'primary' : 'transparent'}
                      className={`rounded-circle d-flex align-items-center justify-content-center padding-0 ${
                        styles.circleButton
                      } ${!allSelected ? styles.circleButtonOutline : ''}
                      `}
                      style={{ '--radius': allSelected ? '16px' : '15px' }}
                      onClick={onSelectAll}
                    >
                      {allSelected && <BsCheck size={20} />}
                    </Button>
                    Select All
                  </div>
                  <div className="d-flex flex-column flex-column-reverse flex-md-row align-items-center gap-2">
                    <Button
                      variant="primary"
                      className={`${styles.circleButton} rounded-circle d-flex align-items-center justify-content-center padding-0`}
                      onClick={onAcknowledge}
                    >
                      <BsCheck size={20} />
                    </Button>
                    Acknowledge
                  </div>
                  <div className="d-flex flex-column flex-column-reverse flex-md-row align-items-center gap-2">
                    <Button
                      variant="primary"
                      className={`${styles.circleButton} rounded-circle d-flex align-items-center justify-content-center padding-0`}
                      onClick={onSilence}
                    >
                      <IoClose size={20} />
                    </Button>
                    Silence
                  </div>
                </div>
              )}
              {maxPageIndex >= 1 && (
                <div className="d-flex align-items-center gap-2 justify-content-center justify-content-lg-start">
                  <Button
                    variant="transparent"
                    className={styles.iconButton}
                    onClick={() => setPageIndex(pageIndex - 1)}
                    disabled={pageIndex === 0}
                  >
                    <FaChevronLeft color={pageIndex === 0 ? 'grey' : 'white'} size={20} />
                  </Button>
                  <div>{`Page ${pageIndex + 1} of ${maxPageIndex + 1}`}</div>
                  <Button
                    variant="transparent"
                    className={styles.iconButton}
                    onClick={() => setPageIndex(pageIndex + 1)}
                    disabled={pageIndex >= maxPageIndex}
                  >
                    <FaChevronRight
                      color={pageIndex >= maxPageIndex ? 'grey' : 'white'}
                      size={20}
                    />
                  </Button>
                </div>
              )}
              <div className="d-flex align-items-center gap-2">
                <div>Limit</div>
                <Form.Select
                  size="sm"
                  className={classNames(styles.selector, 'border-0 text-white')}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  defaultValue={0}
                >
                  {LIMIT_OPTIONS.map((option, index) => (
                    <option key={option} value={index}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center justify-content-lg-end col-12 col-lg-4">
            <Form.Control
              className="rounded-3 border-0 w-auto"
              style={{ width: '150px', height: '30px' }}
              type="search"
              placeholder="Search"
              onChange={(e) => onSearchChanged(e.target.value.replace('CW_', ''))}
            />
          </div>
        </div>

        <div className="gx-0 mt-2 row">
          {headerFields?.map((header) => (
            <div key={header} className={`d-flex flex-column w-100 ${styles.section} col`}>
              {header}
            </div>
          ))}
        </div>
      </div>

      <div className="flex-grow-1 overflow-hidden min-h-0">{children}</div>
    </Container>
  );
}
